import React, { useState, useCallback } from 'react';

const LANG_LABELS = {
  js: 'JavaScript', javascript: 'JavaScript',
  ts: 'TypeScript', typescript: 'TypeScript',
  jsx: 'JSX', tsx: 'TSX',
  py: 'Python', python: 'Python',
  html: 'HTML', css: 'CSS', scss: 'SCSS',
  json: 'JSON', sh: 'Shell', bash: 'Shell',
  sql: 'SQL', md: 'Markdown',
  yaml: 'YAML', yml: 'YAML',
  php: 'PHP', rb: 'Ruby',
  go: 'Go', rs: 'Rust',
  c: 'C', cpp: 'C++',
  vue: 'Vue', svelte: 'Svelte',
  dockerfile: 'Dockerfile', docker: 'Dockerfile',
};

// Languages treated as prose/prompt — wrap instead of scroll horizontally
const TEXT_LANGS = new Set(['', 'text', 'prompt', 'txt', 'plaintext', 'md', 'markdown']);

export const AthenaCodeBlock = React.memo(function AthenaCodeBlock({ children, className }) {
  const [copied, setCopied] = useState(false);

  const rawLang = (className || '').replace('language-', '').toLowerCase();
  const label = LANG_LABELS[rawLang] || (rawLang ? rawLang.toUpperCase() : 'Código');
  const isText = TEXT_LANGS.has(rawLang);
  const codeStr = typeof children === 'string' ? children : String(children ?? '');

  const handleCopy = useCallback(() => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(codeStr.trimEnd()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  }, [codeStr]);

  return (
    <div style={{
      background: 'rgba(6,6,16,0.75)',
      border: '1px solid rgba(255,255,255,0.09)',
      borderRadius: 10,
      margin: '10px 0',
      overflow: 'hidden',
      // Prevent flex parent from growing wider than viewport
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '5px 14px',
        background: 'rgba(255,255,255,0.025)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        userSelect: 'none',
      }}>
        <span style={{
          fontSize: 10,
          fontFamily: 'monospace',
          color: 'rgba(140,160,220,0.6)',
          letterSpacing: '.08em',
          textTransform: 'uppercase',
        }}>
          {label}
        </span>
        <button
          onClick={handleCopy}
          style={{
            background: copied ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${copied ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: 6,
            padding: '3px 10px',
            cursor: 'pointer',
            fontSize: 10.5,
            fontFamily: 'system-ui, sans-serif',
            color: copied ? '#10b981' : 'rgba(160,180,235,0.6)',
            transition: 'all .18s ease',
            touchAction: 'manipulation',
            minWidth: 58,
            outline: 'none',
          }}
        >
          {copied ? '✓ Copiado' : 'Copiar'}
        </button>
      </div>

      {/* Code content */}
      <pre style={{
        margin: 0,
        padding: '14px 16px',
        fontSize: 12,
        lineHeight: 1.7,
        fontFamily: "'Fira Code','JetBrains Mono','Cascadia Code','Consolas',monospace",
        color: '#c4d0f5',
        maxWidth: '100%',
        // Text/prompt: wrap and never overflow horizontally
        // Real code: scroll internally, never push page width
        ...(isText ? {
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          overflowX: 'hidden',
        } : {
          whiteSpace: 'pre',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }),
      }}>
        <code style={{ fontFamily: 'inherit', background: 'none', padding: 0, borderRadius: 0 }}>
          {children}
        </code>
      </pre>
    </div>
  );
});
